import axios, { CancelTokenSource } from 'axios';
import { ResourceGroupDto, UpsertResourceGroupDto } from 'ts-types/api.types';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import FormContainer from 'components/layout/FormContainer';
import styled from 'styled-components';
import FormContent from 'components/layout/FormContent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { Button, Grid } from 'semantic-ui-react';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import { required } from 'utils/validatorUtils';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { editResourceGroup, saveResourceGroup } from 'services/resourceGroupServices';


const UpsertResourceGroupFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface Props extends MessageSourceProps{
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  resourceGroupId?: number,
  resourceGroup?: ResourceGroupDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertResourceGroupForm extends Component<Props,State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  initialResourceGroupValues = (): Partial<UpsertResourceGroupDto> => {
    return {
      active: true,
    }
  }

  render(): React.ReactNode {

    const {resourceGroup = this.initialResourceGroupValues()} = this.props;

    return (
        <FinalForm
        onSubmit={(values) => this.handleSubmit(values)}
        initialValues={resourceGroup}
        subscription={{values: true, pristine: true, submitting: true}}
        render={this.renderForm}
        />
    )
  }

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<UpsertResourceGroupDto>) => {

    let saveValues: Partial<UpsertResourceGroupDto> = {...values}

    const {resourceGroupId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

      if (resourceGroupId) {
        await editResourceGroup(resourceGroupId, saveValues, cancelTokenSource)
            .then(onSave)
            .catch((error) => this.handleError(error.response.data))
      } else {
        await saveResourceGroup(saveValues, cancelTokenSource)
            .then(onSave)
            .catch((error) => this.handleError(error.response.data))
      }
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {onCancel, getMessage} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
            <FormContainer>
              <UpsertResourceGroupFormContent>
                {
                  errorMessage && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
                }

                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={12}></Grid.Column>
                    <Grid.Column width={4} textAlign="right">
                      <Field
                          name="active"
                          component={CheckBox}
                          label={getMessage("resourceGroups.active")}
                          toggle={true}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("resourceGroups.description")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="description"
                          component={Input}
                          validate={required}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}></Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          name="useInPatientCentral"
                          component={CheckBox}
                          label={`${getMessage("resourceGroups.useInPatientCentral")}`}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
              </UpsertResourceGroupFormContent>

              <FormButtonsRow>
                <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
                <Button compact type="button" disabled={submitting}
                        onClick={onCancel}>{getMessage("button.cancel")}</Button>
              </FormButtonsRow>
            </FormContainer>
        </form>
    )}
}

export default withMessages(UpsertResourceGroupForm);
